import { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Sparkles, X } from "lucide-react";

/**
 * EndorseDialog — opens from another provider's profile (only visible if
 * viewer is Verified Initiate or Elder). POSTs to /api/endorsements with a
 * short public statement.
 *
 * Props:
 *   - providerId  (endorsee)
 *   - providerName
 *   - onDone (fired after successful endorsement)
 */
export default function EndorseDialog({ providerId, providerName, onDone }) {
  const [open, setOpen] = useState(false);
  const [statement, setStatement] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/endorsements", {
        endorseeId: providerId,
        statement: statement.trim() || undefined,
      });
      toast.success("Endorsement posted");
      setOpen(false);
      setStatement("");
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to endorse");
    } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 border border-violet-300/40 text-violet-200 px-3 py-1.5 rounded-sm uppercase tracking-wider text-[11px] hover:bg-violet-300/10"
        data-testid="endorse-open"
      >
        <Sparkles size={12} strokeWidth={1.5} /> Endorse
      </button>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm" data-testid="endorse-dialog">
      <div className="bg-card border border-secondary/30 rounded-sm p-6 w-full max-w-md space-y-4 mx-4 relative">
        <button onClick={() => setOpen(false)} className="absolute top-3 right-3 text-foreground/60 hover:text-foreground" data-testid="endorse-close">
          <X size={18} />
        </button>
        <div>
          <div className="label-mystic">Public endorsement</div>
          <h2 className="font-serif text-2xl mt-1">Endorse {providerName}</h2>
        </div>
        <p className="text-sm text-foreground/70">
          Your endorsement is permanent and public. It carries weight because you are
          a Verified Initiate or Elder. Use this for practitioners you have personally
          trained, initiated, or worked alongside.
        </p>
        <textarea
          value={statement}
          onChange={(e) => setStatement(e.target.value)}
          rows={4}
          maxLength={500}
          placeholder="Initiated under my house in 2014. Trained in Ifá divination..."
          className="w-full bg-input border border-secondary/20 px-3 py-2 rounded-sm focus:outline-none focus:border-secondary text-sm"
          data-testid="endorse-statement"
        />
        <div className="flex justify-end gap-2">
          <button
            onClick={() => setOpen(false)}
            className="text-foreground/60 px-4 py-2 text-xs uppercase tracking-wider"
            data-testid="endorse-cancel"
          >Cancel</button>
          <button
            onClick={submit}
            disabled={busy}
            className="bg-primary text-primary-foreground px-5 py-2 rounded-sm uppercase tracking-wider text-xs disabled:opacity-50"
            data-testid="endorse-submit"
          >{busy ? "Posting..." : "Post Endorsement"}</button>
        </div>
      </div>
    </div>
  );
}
